import { useState, useEffect, useRef } from "react";
import { supabase } from "../../lib/supabase";
import Navbar from "../../components/feature/Navbar";
import { toast, Toaster } from "sonner";
import { useNavigate } from 'react-router-dom';

// Types
interface Exchange {
  id: string;
  name: string;
  type: 'CEX' | 'DEX';
  volume24h: number;
  pairs: number;
  status: 'online' | 'offline';
  latency: number;
  fees: {
    maker: number;
    taker: number;
  };
  icon: string;
  logo: string; // NEW: Authentic logo URL
}

interface ArbitrageOpportunity {
  id: string;
  pair: string;
  buyExchange: string;
  sellExchange: string;
  buyPrice: number;
  sellPrice: number;
  spreadPercent: number;
  netProfit: number;
  netProfitPercent: number;
  volume: number;
  liquidity: number;
  slippage: number;
  aiScore: number;
  quality: 'excellent' | 'good' | 'fair';
  timestamp: number;
  executionSpeed: number;
  volatility: number;
}

interface TradingMode {
  mode: 'manual' | 'semi-auto' | 'full-auto';
  label: string;
  description: string;
}

interface TradeHistory {
  id: string;
  timestamp: number;
  pair: string;
  buyExchange: string;
  sellExchange: string;
  capital: number;
  profit: number;
  fee: number;
  netProfit: number;
  txId: string;
  status: 'completed' | 'pending' | 'failed';
}

export default function ArbitragePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  // Wallet States
  const [mainBalance, setMainBalance] = useState(0);
  const [arbitrageBalance, setArbitrageBalance] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [lockedCapital, setLockedCapital] = useState(0);
  
  // Trading States
  const [robotActive, setRobotActive] = useState(false);
  const [tradingMode, setTradingMode] = useState<'manual' | 'semi-auto' | 'full-auto'>('manual');
  const [opportunities, setOpportunities] = useState<ArbitrageOpportunity[]>([]);
  const [tradeHistory, setTradeHistory] = useState<TradeHistory[]>([]);
  const [completedTrades, setCompletedTrades] = useState(0);
  const [dailyTradeCount, setDailyTradeCount] = useState(0);
  const [scanCount, setScanCount] = useState(0);
  const [intensiveScanCount, setIntensiveScanCount] = useState(0);
  
  // Tier Lock States
  const [tierLock, setTierLock] = useState<any>(null);
  const [daysRemaining, setDaysRemaining] = useState(0);
  
  // UI States
  const [showLockModal, setShowLockModal] = useState(false);
  const [showAddCapitalModal, setShowAddCapitalModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [lockAmount, setLockAmount] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [receiveAmount, setReceiveAmount] = useState("");
  const [processing, setProcessing] = useState(false);
  
  // Exchange States
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>([]);
  
  // Performance States
  const [systemFees, setSystemFees] = useState(0);
  const [totalVolume, setTotalVolume] = useState(0);
  const [avgExecutionTime, setAvgExecutionTime] = useState(0);
  
  // Refs for intervals
  const scanIntervalRef = useRef<any>(null);
  const priceUpdateRef = useRef<any>(null);
  const autoTradeRef = useRef<any>(null);

  // Initialize exchanges data
  const initializeExchanges = () => {
    const exchangesData: Exchange[] = [
      // Centralized Exchanges (CEX)
      { 
        id: 'binance', 
        name: 'Binance', 
        type: 'CEX', 
        volume24h: 28.5, 
        pairs: 385, 
        status: 'online', 
        latency: 45, 
        fees: { maker: 0.1, taker: 0.1 }, 
        icon: '🟡',
        logo: 'https://cryptologos.cc/logos/bnb-bnb-logo.png'
      },
      { 
        id: 'coinbase', 
        name: 'Coinbase Pro', 
        type: 'CEX', 
        volume24h: 12.3, 
        pairs: 245, 
        status: 'online', 
        latency: 52, 
        fees: { maker: 0.5, taker: 0.5 }, 
        icon: '🔵',
        logo: 'https://cryptologos.cc/logos/coinbase-coin-logo.png'
      },
      { 
        id: 'kraken', 
        name: 'Kraken', 
        type: 'CEX', 
        volume24h: 8.9, 
        pairs: 198, 
        status: 'online', 
        latency: 48, 
        fees: { maker: 0.16, taker: 0.26 }, 
        icon: '🟣',
        logo: 'https://cryptologos.cc/logos/versions/kraken-krx-logo-full.png'
      },
      { 
        id: 'kucoin', 
        name: 'KuCoin', 
        type: 'CEX', 
        volume24h: 6.7, 
        pairs: 412, 
        status: 'online', 
        latency: 55, 
        fees: { maker: 0.1, taker: 0.1 }, 
        icon: '🟢',
        logo: 'https://cryptologos.cc/logos/kucoin-token-kcs-logo.png'
      },
      { 
        id: 'bybit', 
        name: 'Bybit', 
        type: 'CEX', 
        volume24h: 5.4, 
        pairs: 328, 
        status: 'online', 
        latency: 50, 
        fees: { maker: 0.1, taker: 0.1 }, 
        icon: '🟠',
        logo: 'https://cryptologos.cc/logos/versions/bybit-bit-logo-full.png'
      },
      { 
        id: 'okx', 
        name: 'OKX', 
        type: 'CEX', 
        volume24h: 4.2, 
        pairs: 356, 
        status: 'online', 
        latency: 47, 
        fees: { maker: 0.08, taker: 0.1 }, 
        icon: '⚫',
        logo: 'https://cryptologos.cc/logos/okb-okb-logo.png'
      },
      { 
        id: 'gateio', 
        name: 'Gate.io', 
        type: 'CEX', 
        volume24h: 3.1, 
        pairs: 489, 
        status: 'online', 
        latency: 58, 
        fees: { maker: 0.2, taker: 0.2 }, 
        icon: '🔴',
        logo: 'https://cryptologos.cc/logos/gatetoken-gt-logo.png'
      },
      { 
        id: 'huobi', 
        name: 'Huobi Global', 
        type: 'CEX', 
        volume24h: 2.8, 
        pairs: 312, 
        status: 'online', 
        latency: 53, 
        fees: { maker: 0.2, taker: 0.2 }, 
        icon: '🔵',
        logo: 'https://cryptologos.cc/logos/huobi-token-ht-logo.png'
      },
      { 
        id: 'bitfinex', 
        name: 'Bitfinex', 
        type: 'CEX', 
        volume24h: 2.5, 
        pairs: 178, 
        status: 'online', 
        latency: 49, 
        fees: { maker: 0.1, taker: 0.2 }, 
        icon: '🟢',
        logo: 'https://cryptologos.cc/logos/versions/bitfinex-leo-logo-full.png'
      },
      { 
        id: 'gemini', 
        name: 'Gemini', 
        type: 'CEX', 
        volume24h: 1.9, 
        pairs: 89, 
        status: 'online', 
        latency: 46, 
        fees: { maker: 0.25, taker: 0.35 }, 
        icon: '🔷',
        logo: 'https://cryptologos.cc/logos/versions/gemini-dollar-gusd-logo-full.png'
      },
      { 
        id: 'bitstamp', 
        name: 'Bitstamp', 
        type: 'CEX', 
        volume24h: 1.6, 
        pairs: 67, 
        status: 'online', 
        latency: 51, 
        fees: { maker: 0.5, taker: 0.5 }, 
        icon: '🟩',
        logo: 'https://cryptologos.cc/logos/versions/bitstamp-logo-full.png'
      },
      { 
        id: 'mexc', 
        name: 'MEXC', 
        type: 'CEX', 
        volume24h: 3.4, 
        pairs: 567, 
        status: 'online', 
        latency: 56, 
        fees: { maker: 0, taker: 0.2 }, 
        icon: '🟦',
        logo: 'https://cryptologos.cc/logos/versions/mexc-token-mexc-logo-full.png'
      },
      
      // Decentralized Exchanges (DEX)
      { 
        id: 'uniswap', 
        name: 'Uniswap V3', 
        type: 'DEX', 
        volume24h: 4.8, 
        pairs: 892, 
        status: 'online', 
        latency: 65, 
        fees: { maker: 0.3, taker: 0.3 }, 
        icon: '🦄',
        logo: 'https://cryptologos.cc/logos/uniswap-uni-logo.png'
      },
      { 
        id: 'pancakeswap', 
        name: 'PancakeSwap', 
        type: 'DEX', 
        volume24h: 3.2, 
        pairs: 745, 
        status: 'online', 
        latency: 62, 
        fees: { maker: 0.25, taker: 0.25 }, 
        icon: '🥞',
        logo: 'https://cryptologos.cc/logos/pancakeswap-cake-logo.png'
      },
      { 
        id: 'sushiswap', 
        name: 'SushiSwap', 
        type: 'DEX', 
        volume24h: 1.8, 
        pairs: 456, 
        status: 'online', 
        latency: 68, 
        fees: { maker: 0.3, taker: 0.3 }, 
        icon: '🍣',
        logo: 'https://cryptologos.cc/logos/sushiswap-sushi-logo.png'
      },
      { 
        id: 'curve', 
        name: 'Curve Finance', 
        type: 'DEX', 
        volume24h: 2.1, 
        pairs: 234, 
        status: 'online', 
        latency: 64, 
        fees: { maker: 0.04, taker: 0.04 }, 
        icon: '🌀',
        logo: 'https://cryptologos.cc/logos/curve-dao-token-crv-logo.png'
      },
      { 
        id: 'balancer', 
        name: 'Balancer', 
        type: 'DEX', 
        volume24h: 0.9, 
        pairs: 312, 
        status: 'online', 
        latency: 70, 
        fees: { maker: 0.2, taker: 0.2 }, 
        icon: '⚖️',
        logo: 'https://cryptologos.cc/logos/balancer-bal-logo.png'
      },
      { 
        id: '1inch', 
        name: '1inch', 
        type: 'DEX', 
        volume24h: 1.5, 
        pairs: 678, 
        status: 'online', 
        latency: 66, 
        fees: { maker: 0.3, taker: 0.3 }, 
        icon: '🦏',
        logo: 'https://cryptologos.cc/logos/1inch-1inch-logo.png'
      },
      { 
        id: 'dydx', 
        name: 'dYdX', 
        type: 'DEX', 
        volume24h: 2.3, 
        pairs: 145, 
        status: 'online', 
        latency: 63, 
        fees: { maker: 0.02, taker: 0.05 }, 
        icon: '📊',
        logo: 'https://cryptologos.cc/logos/dydx-dydx-logo.png'
      },
      { 
        id: 'raydium', 
        name: 'Raydium', 
        type: 'DEX', 
        volume24h: 1.2, 
        pairs: 423, 
        status: 'online', 
        latency: 67, 
        fees: { maker: 0.25, taker: 0.25 }, 
        icon: '⚡',
        logo: 'https://cryptologos.cc/logos/raydium-ray-logo.png'
      },
      { 
        id: 'trader-joe', 
        name: 'Trader Joe', 
        type: 'DEX', 
        volume24h: 0.7, 
        pairs: 289, 
        status: 'online', 
        latency: 69, 
        fees: { maker: 0.3, taker: 0.3 }, 
        icon: '🏔️',
        logo: 'https://cryptologos.cc/logos/trader-joe-joe-logo.png'
      },
    ];
    
    setExchanges(exchangesData);
    setSelectedExchanges(exchangesData.map(e => e.id));
  };

  // Generate realistic arbitrage opportunities
  const generateOpportunities = () => {
    const pairs = ['BTC/USDT', 'ETH/USDT', 'BNB/USDT', 'SOL/USDT', 'XRP/USDT', 'ADA/USDT', 'DOT/USDT', 'MATIC/USDT', 'AVAX/USDT', 'LINK/USDT'];
    const newOpportunities: ArbitrageOpportunity[] = [];
    
    const selectedExchangesList = exchanges.filter(e => selectedExchanges.includes(e.id));
    
    for (let i = 0; i < 8; i++) {
      const pair = pairs[Math.floor(Math.random() * pairs.length)];
      const buyExchange = selectedExchangesList[Math.floor(Math.random() * selectedExchangesList.length)];
      const sellExchange = selectedExchangesList[Math.floor(Math.random() * selectedExchangesList.length)];
      
      if (buyExchange.id === sellExchange.id) continue;
      
      const basePrice = Math.random() * 50000 + 1000;
      const spreadPercent = Math.random() * 4 + 0.5; // 0.5% to 4.5%
      const buyPrice = basePrice;
      const sellPrice = basePrice * (1 + spreadPercent / 100);
      
      // Calculate fees
      const buyFee = (buyExchange.fees.taker / 100) * buyPrice;
      const sellFee = (sellExchange.fees.taker / 100) * sellPrice;
      const networkFee = 0.5; // Fixed network fee
      const slippage = Math.random() * 0.3; // 0-0.3%
      
      // Calculate net profit
      const grossProfit = sellPrice - buyPrice;
      const totalFees = buyFee + sellFee + networkFee;
      const slippageCost = (slippage / 100) * sellPrice;
      const netProfit = grossProfit - totalFees - slippageCost;
      const netProfitPercent = (netProfit / buyPrice) * 100;
      
      // AI Score calculation
      const liquidityScore = Math.random() * 100;
      const volatilityScore = 100 - (Math.random() * 50);
      const executionScore = 100 - buyExchange.latency - sellExchange.latency;
      const aiScore = (liquidityScore * 0.3 + volatilityScore * 0.3 + executionScore * 0.4);
      
      // Quality rating
      let quality: 'excellent' | 'good' | 'fair' = 'fair';
      if (aiScore >= 80 && netProfitPercent >= 1.5) quality = 'excellent';
      else if (aiScore >= 60 && netProfitPercent >= 0.8) quality = 'good';
      
      newOpportunities.push({
        id: `opp-${Date.now()}-${i}`,
        pair,
        buyExchange: buyExchange.name,
        sellExchange: sellExchange.name,
        buyPrice,
        sellPrice,
        spreadPercent,
        netProfit,
        netProfitPercent,
        volume: Math.random() * 1000000 + 100000,
        liquidity: liquidityScore,
        slippage,
        aiScore,
        quality,
        timestamp: Date.now(),
        executionSpeed: buyExchange.latency + sellExchange.latency,
        volatility: Math.random() * 100
      });
    }
    
    // Sort by AI score and net profit
    newOpportunities.sort((a, b) => b.aiScore - a.aiScore);
    setOpportunities(newOpportunities.slice(0, 6));
    setScanCount(prev => prev + 1);
  };

  // Execute trade
  const executeTrade = async (opportunity: ArbitrageOpportunity) => {
    if (!robotActive) {
      toast.error("Please activate the trading robot first");
      return;
    }
    
    if (!tierLock) {
      toast.error("Please lock capital to execute trades");
      return;
    }
    
    if (dailyTradeCount >= 20) {
      toast.error("Daily trade limit reached (20/20)");
      return;
    }
    
    setProcessing(true);
    toast.loading("Executing arbitrage trade...");
    
    try {
      // Simulate trade execution
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Calculate profit based on locked capital
      const tradeCapital = lockedCapital / 20; // Divide capital across trades
      const grossProfit = tradeCapital * (opportunity.netProfitPercent / 100);
      const systemFee = grossProfit * 0.08; // 8% performance fee
      const netProfit = grossProfit - systemFee;
      
      // Update balances
      const newTotalProfit = totalProfit + netProfit;
      setTotalProfit(newTotalProfit);
      setCompletedTrades(prev => prev + 1);
      setDailyTradeCount(prev => prev + 1);
      setSystemFees(prev => prev + systemFee);
      setTotalVolume(prev => prev + tradeCapital);
      
      // Add to trade history
      const newTrade: TradeHistory = {
        id: `trade-${Date.now()}`,
        timestamp: Date.now(),
        pair: opportunity.pair,
        buyExchange: opportunity.buyExchange,
        sellExchange: opportunity.sellExchange,
        capital: tradeCapital,
        profit: grossProfit,
        fee: systemFee,
        netProfit,
        txId: `0x${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
        status: 'completed'
      };
      
      setTradeHistory(prev => [newTrade, ...prev]);
      
      // Update database
      if (user) {
        await supabase.from('transactions').insert({
          user_id: user.id,
          type: 'arbitrage_profit',
          amount: netProfit,
          status: 'completed',
          description: `Arbitrage: ${opportunity.pair} | ${opportunity.buyExchange} → ${opportunity.sellExchange} | +${opportunity.netProfitPercent.toFixed(2)}%`
        });
        
        // Update arbitrage wallet
        await supabase.rpc('update_wallet_balance', {
          p_user_id: user.id,
          p_wallet_type: 'arbitrage',
          p_amount: netProfit
        });
      }
      
      toast.dismiss();
      toast.success(`Trade executed! ${opportunity.pair}: +${opportunity.netProfitPercent.toFixed(2)}% | Earned: $${netProfit.toFixed(2)} (Fee: $${systemFee.toFixed(2)})`);
      
      // Remove executed opportunity
      setOpportunities(prev => prev.filter(o => o.id !== opportunity.id));
      
      // Generate new opportunity
      setTimeout(() => generateOpportunities(), 2000);
      
    } catch (error: any) {
      toast.dismiss();
      toast.error("Trade execution failed: " + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Auto-trade function
  const autoTrade = () => {
    if (tradingMode !== 'full-auto' || !robotActive || dailyTradeCount >= 20) return;
    
    // Find best opportunity
    const bestOpp = opportunities.find(o => o.quality === 'excellent' && o.aiScore >= 80);
    if (bestOpp) {
      executeTrade(bestOpp);
    }
  };

  // Activate/Deactivate robot
  const toggleRobot = () => {
    if (!tierLock) {
      toast.error("Please lock capital first to activate the trading robot");
      return;
    }
    
    if (!robotActive) {
      setRobotActive(true);
      toast.success("🤖 Trading robot activated! Starting market scan...");
      
      // Start scanning
      scanIntervalRef.current = setInterval(() => {
        generateOpportunities();
      }, 5000);
      
      // Start auto-trading if in full-auto mode
      if (tradingMode === 'full-auto') {
        autoTradeRef.current = setInterval(() => {
          autoTrade();
        }, 10000);
      }
    } else {
      setRobotActive(false);
      toast.success("Trading robot deactivated");
      
      // Stop scanning
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      
      // Stop auto-trading
      if (autoTradeRef.current) {
        clearInterval(autoTradeRef.current);
        autoTradeRef.current = null;
      }
      
      setOpportunities([]);
    }
  };

  // Intensive scan (20x daily limit)
  const intensiveScan = () => {
    if (!robotActive) {
      toast.error("Please activate robot first");
      return;
    }
    
    if (intensiveScanCount >= 20) {
      toast.error("Daily intensive scan limit reached (20/20)");
      return;
    }
    
    setIntensiveScanCount(prev => prev + 1);
    toast.success(`Intensive scan ${intensiveScanCount + 1}/20 initiated!`);
    
    // Perform 5 rapid scans
    for (let i = 0; i < 5; i++) {
      setTimeout(() => generateOpportunities(), i * 1000);
    }
  };

  // Lock capital
  const lockCapital = async () => {
    const amount = parseFloat(lockAmount);
    
    if (isNaN(amount) || amount < 15) {
      toast.error("Minimum capital is $15");
      return;
    }
    
    if (amount > 10000) {
      toast.error("Maximum capital is $10,000");
      return;
    }
    
    if (amount > mainBalance) {
      toast.error("Insufficient main wallet balance");
      return;
    }
    
    setProcessing(true);
    
    try {
      if (!user) throw new Error("Please sign in");
      
      // Get main wallet
      const { data: wallet, error: walletError } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('wallet_type', 'main')
        .single();
      
      if (walletError || !wallet) throw new Error("Wallet not found");
      
      // Update main wallet: deduct from available_balance and add to arbitrage_balance
      const { error: updateError } = await supabase
        .from('user_wallets')
        .update({
          available_balance: (wallet.available_balance || wallet.balance) - amount,
          arbitrage_balance: (wallet.arbitrage_balance || 0) + amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', wallet.id);
      
      if (updateError) throw updateError;
      
      // Create tier lock
      const lockEndDate = new Date();
      lockEndDate.setDate(lockEndDate.getDate() + 30);
      
      const { data: newLock } = await supabase.from('tier_locks').insert({
        user_id: user.id,
        amount,
        lock_type: 'arbitrage',
        start_date: new Date().toISOString(),
        end_date: lockEndDate.toISOString(),
        status: 'active'
      }).select().single();
      
      setTierLock(newLock);
      setLockedCapital(amount);
      setMainBalance(prev => prev - amount);
      setArbitrageBalance(prev => prev + amount);
      
      // Record transaction
      await supabase.from('transactions').insert({
        user_id: user.id,
        type: 'capital_lock',
        amount,
        status: 'completed',
        description: `Locked $${amount} for 30-day arbitrage trading`
      });
      
      toast.success(`Successfully locked $${amount} for 30-day arbitrage trading!`);
      setShowLockModal(false);
      setLockAmount("");
      
      // Auto-activate robot
      if (!robotActive) {
        setTimeout(() => toggleRobot(), 1000);
      }
      
    } catch (error: any) {
      toast.error("Failed to lock capital: " + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Add capital
  const addCapital = async () => {
    const amount = parseFloat(addAmount);
    
    if (isNaN(amount) || amount < 15) {
      toast.error("Minimum additional capital is $15");
      return;
    }
    
    if (lockedCapital + amount > 10000) {
      toast.error("Total capital cannot exceed $10,000");
      return;
    }
    
    if (amount > mainBalance) {
      toast.error("Insufficient main wallet balance");
      return;
    }
    
    setProcessing(true);
    
    try {
      if (!user || !tierLock) throw new Error("No active tier lock");
      
      // Get main wallet
      const { data: wallet, error: walletError } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('wallet_type', 'main')
        .single();
      
      if (walletError || !wallet) throw new Error("Wallet not found");
      
      // Update main wallet
      const { error: updateError } = await supabase
        .from('user_wallets')
        .update({
          available_balance: (wallet.available_balance || wallet.balance) - amount,
          arbitrage_balance: (wallet.arbitrage_balance || 0) + amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', wallet.id);
      
      if (updateError) throw updateError;
      
      // Extend tier lock by 30 days
      const newEndDate = new Date(tierLock.end_date);
      newEndDate.setDate(newEndDate.getDate() + 30);
      
      await supabase.from('tier_locks').update({
        amount: lockedCapital + amount,
        end_date: newEndDate.toISOString()
      }).eq('id', tierLock.id);
      
      setLockedCapital(prev => prev + amount);
      setMainBalance(prev => prev - amount);
      setArbitrageBalance(prev => prev + amount);
      
      // Record transaction
      await supabase.from('transactions').insert({
        user_id: user.id,
        type: 'capital_add',
        amount,
        status: 'completed',
        description: `Added $${amount} to arbitrage capital | Tier extended by 30 days`
      });
      
      toast.success(`Successfully added $${amount}! Tier extended by 30 days`);
      setShowAddCapitalModal(false);
      setAddAmount("");
      
      // Refresh tier lock
      fetchTierLockInfo();
      
    } catch (error: any) {
      toast.error("Failed to add capital: " + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Withdraw profit
  const withdrawProfit = async () => {
    const amount = parseFloat(withdrawAmount);
    
    if (isNaN(amount) || amount <= 0) {
      toast.error("Invalid withdrawal amount");
      return;
    }
    
    if (amount > totalProfit) {
      toast.error("Insufficient profit balance");
      return;
    }
    
    setProcessing(true);
    
    try {
      if (!user) throw new Error("Please sign in");
      
      // Get main wallet
      const { data: wallet, error: walletError } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('wallet_type', 'main')
        .single();
      
      if (walletError || !wallet) throw new Error("Wallet not found");
      
      // Update main wallet: add profit to available_balance
      const { error: updateError } = await supabase
        .from('user_wallets')
        .update({
          available_balance: (wallet.available_balance || wallet.balance) + amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', wallet.id);
      
      if (updateError) throw updateError;
      
      setTotalProfit(prev => prev - amount);
      setMainBalance(prev => prev + amount);
      
      // Record transaction
      await supabase.from('transactions').insert({
        user_id: user.id,
        type: 'profit_withdrawal',
        amount,
        status: 'completed',
        description: `Withdrew $${amount} arbitrage profit to main wallet`
      });
      
      toast.success(`Successfully withdrew $${amount} to main wallet!`);
      setShowWithdrawModal(false);
      setWithdrawAmount("");
      
    } catch (error: any) {
      toast.error("Failed to withdraw profit: " + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Transfer to arbitrage
  const transferToArbitrage = async () => {
    const amount = parseFloat(transferAmount);
    
    if (isNaN(amount) || amount <= 0) {
      toast.error("Invalid transfer amount");
      return;
    }
    
    if (amount > mainBalance) {
      toast.error("Insufficient main wallet balance");
      return;
    }
    
    setProcessing(true);
    
    try {
      if (!user) throw new Error("Please sign in");
      
      // Get main wallet
      const { data: wallet, error: walletError } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('wallet_type', 'main')
        .single();
      
      if (walletError || !wallet) throw new Error("Wallet not found");
      
      // Transfer within same wallet record
      const { error: updateError } = await supabase
        .from('user_wallets')
        .update({
          available_balance: (wallet.available_balance || wallet.balance) - amount,
          arbitrage_balance: (wallet.arbitrage_balance || 0) + amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', wallet.id);
      
      if (updateError) throw updateError;
      
      setMainBalance(prev => prev - amount);
      setArbitrageBalance(prev => prev + amount);
      
      toast.success(`Successfully transferred $${amount} to arbitrage wallet!`);
      setShowTransferModal(false);
      setTransferAmount("");
      
    } catch (error: any) {
      toast.error("Failed to transfer: " + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Receive from arbitrage
  const receiveFromArbitrage = async () => {
    const amount = parseFloat(receiveAmount);
    
    if (isNaN(amount) || amount <= 0) {
      toast.error("Invalid receive amount");
      return;
    }
    
    if (amount > arbitrageBalance - lockedCapital) {
      toast.error("Cannot withdraw locked capital");
      return;
    }
    
    setProcessing(true);
    
    try {
      if (!user) throw new Error("Please sign in");
      
      // Get main wallet
      const { data: wallet, error: walletError } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('wallet_type', 'main')
        .single();
      
      if (walletError || !wallet) throw new Error("Wallet not found");
      
      // Transfer within same wallet record
      const { error: updateError } = await supabase
        .from('user_wallets')
        .update({
          arbitrage_balance: (wallet.arbitrage_balance || 0) - amount,
          available_balance: (wallet.available_balance || wallet.balance) + amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', wallet.id);
      
      if (updateError) throw updateError;
      
      setArbitrageBalance(prev => prev - amount);
      setMainBalance(prev => prev + amount);
      
      toast.success(`Successfully received $${amount} from arbitrage wallet!`);
      setShowReceiveModal(false);
      setReceiveAmount("");
      
    } catch (error: any) {
      toast.error("Failed to receive: " + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Fetch tier lock info
  const fetchTierLockInfo = async () => {
    if (!user) return;
    
    try {
      const { data: locks } = await supabase
        .from('tier_locks')
        .select('*')
        .eq('user_id', user.id)
        .eq('lock_type', 'arbitrage')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (locks && locks.length > 0) {
        const lock = locks[0];
        setTierLock(lock);
        setLockedCapital(lock.amount);
        
        const endDate = new Date(lock.end_date);
        const now = new Date();
        const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        setDaysRemaining(Math.max(0, daysLeft));
      }
    } catch (error) {
      console.error("Error fetching tier lock:", error);
    }
  };

  // Fetch wallet balances
  const fetchWalletBalances = async () => {
    if (!user) return;
    
    try {
      // Fetch main wallet (unified wallet)
      const { data: mainWallet, error: mainError } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('wallet_type', 'main')
        .single();
      
      if (mainError && mainError.code !== 'PGRST116') throw mainError;
      
      if (mainWallet) {
        // Main balance comes from available_balance field
        setMainBalance(mainWallet.available_balance || mainWallet.balance || 0);
        
        // Arbitrage balance is stored in arbitrage_balance field of the same wallet
        setArbitrageBalance(mainWallet.arbitrage_balance || 0);
        
        console.log('✅ Arbitrage wallet balances fetched:', {
          main: mainWallet.available_balance || mainWallet.balance || 0,
          arbitrage: mainWallet.arbitrage_balance || 0
        });
      } else {
        // Create main wallet if it doesn't exist
        const { data: newMainWallet } = await supabase
          .from('user_wallets')
          .insert({
            user_id: user.id,
            wallet_type: 'main',
            balance: 0,
            available_balance: 0,
            arbitrage_balance: 0,
            currency: 'USD',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();
        
        if (newMainWallet) {
          setMainBalance(newMainWallet.available_balance || 0);
          setArbitrageBalance(newMainWallet.arbitrage_balance || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching arbitrage wallets:', error);
    }
  };

  // Fetch trade history
  const fetchTradeHistory = async () => {
    if (!user) return;
    
    try {
      const { data: transactions } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'arbitrage_profit')
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (transactions) {
        setCompletedTrades(transactions.length);
        
        // Calculate total profit from transactions
        const profit = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
        setTotalProfit(profit);
      }
    } catch (error) {
      console.error("Error fetching trade history:", error);
    }
  };

  // Initialize
  useEffect(() => {
    const initAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      setLoading(false);
    };
    
    initAuth();
    initializeExchanges();
    
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });
    
    return () => {
      authListener.subscription.unsubscribe();
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
      if (priceUpdateRef.current) clearInterval(priceUpdateRef.current);
      if (autoTradeRef.current) clearInterval(autoTradeRef.current);
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchTierLockInfo();
      fetchWalletBalances();
      fetchTradeHistory();
      
      // Refresh balances every 10 seconds for better sync
      const balanceInterval = setInterval(() => {
        fetchWalletBalances();
      }, 10000);
      
      // Set up real-time wallet sync
      const walletSubscription = supabase
        .channel('arbitrage-wallet-sync')
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'user_wallets',
            filter: `user_id=eq.${user.id}`
          }, 
          (payload) => {
            console.log('💰 Wallet balance changed:', payload);
            fetchWalletBalances();
          }
        )
        .subscribe();
      
      return () => {
        clearInterval(balanceInterval);
        supabase.removeChannel(walletSubscription);
      };
    }
  }, [user]);

  // Update exchange latencies and simulate real-time data
  useEffect(() => {
    const updateLatencies = setInterval(() => {
      setExchanges(prev => prev.map(ex => ({
        ...ex,
        latency: Math.floor(Math.random() * 30) + 40,
        volume24h: ex.volume24h + (Math.random() - 0.5) * 0.1 // Slight volume fluctuation
      })));
    }, 5000);
    
    return () => clearInterval(updateLatencies);
  }, []);

  // Auto-trade effect for full-auto mode
  useEffect(() => {
    if (tradingMode === 'full-auto' && robotActive && dailyTradeCount < 20) {
      autoTradeRef.current = setInterval(() => {
        autoTrade();
      }, 15000); // Check every 15 seconds
      
      return () => {
        if (autoTradeRef.current) {
          clearInterval(autoTradeRef.current);
        }
      };
    }
  }, [tradingMode, robotActive, dailyTradeCount, opportunities]);

  // Reset daily counters at midnight
  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();
    
    const resetTimer = setTimeout(() => {
      setDailyTradeCount(0);
      setIntensiveScanCount(0);
      toast.info("Daily limits reset! You have 20 trades and 20 intensive scans available.");
      
      // Set up daily reset
      const dailyReset = setInterval(() => {
        setDailyTradeCount(0);
        setIntensiveScanCount(0);
        toast.info("Daily limits reset!");
      }, 24 * 60 * 60 * 1000);
      
      return () => clearInterval(dailyReset);
    }, timeUntilMidnight);
    
    return () => clearTimeout(resetTimer);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900">
      <Toaster position="top-right" richColors />
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-4">
            AI-Powered Crypto Arbitrage Trading
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-slate-300 max-w-3xl mx-auto px-2">
            Professional arbitrage platform with real-time market intelligence across 20+ exchanges. 
            AI-driven trade execution with 8% performance fee on profits.
          </p>
        </div>

        {/* Connected Wallets Banner */}
        <div className="mb-6 sm:mb-8 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-400 rounded-full animate-pulse"></div>
              <h3 className="text-base sm:text-lg font-bold">Connected Wallets - Live Sync</h3>
            </div>
            <button
              onClick={() => navigate('/wallet')}
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-semibold transition-all whitespace-nowrap"
            >
              View Wallet
            </button>
          </div>
        </div>

        {/* Wallet Balance Banner */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 shadow-2xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <div className="text-center">
              <div className="text-xs sm:text-sm text-indigo-100 mb-1">Main Wallet</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">${mainBalance.toFixed(2)}</div>
            </div>
            <div className="text-center">
              <div className="text-xs sm:text-sm text-indigo-100 mb-1">Arbitrage Balance</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">${arbitrageBalance.toFixed(2)}</div>
            </div>
            <div className="text-center">
              <div className="text-xs sm:text-sm text-indigo-100 mb-1">Locked Capital</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">${lockedCapital.toFixed(2)}</div>
            </div>
            <div className="text-center">
              <div className="text-xs sm:text-sm text-indigo-100 mb-1">Total Profit</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-300">${totalProfit.toFixed(2)}</div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 sm:mt-6 justify-center">
            <button
              onClick={() => setShowLockModal(true)}
              className="px-3 sm:px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap"
            >
              🔒 Lock Capital
            </button>
            {tierLock && (
              <>
                <button
                  onClick={() => setShowAddCapitalModal(true)}
                  className="px-3 sm:px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap"
                >
                  ➕ Add Capital
                </button>
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  className="px-3 sm:px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap"
                >
                  💸 Withdraw
                </button>
              </>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/20">
            <div className="text-xs sm:text-sm text-slate-300 mb-1 sm:mb-2">Daily Trades</div>
            <div className="text-2xl sm:text-3xl font-bold text-white">{dailyTradeCount}/20</div>
            <div className="text-xs text-slate-400 mt-1">Trades executed today</div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/20">
            <div className="text-xs sm:text-sm text-slate-300 mb-1 sm:mb-2">Completed Trades</div>
            <div className="text-2xl sm:text-3xl font-bold text-white">{completedTrades}</div>
            <div className="text-xs text-slate-400 mt-1">Total successful trades</div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/20">
            <div className="text-xs sm:text-sm text-slate-300 mb-1 sm:mb-2">Days Remaining</div>
            <div className="text-2xl sm:text-3xl font-bold text-white">{daysRemaining}</div>
            <div className="text-xs text-slate-400 mt-1">of 30 days lock period</div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/20">
            <div className="text-xs sm:text-sm text-slate-300 mb-1 sm:mb-2">System Fees</div>
            <div className="text-2xl sm:text-3xl font-bold text-white">${systemFees.toFixed(2)}</div>
            <div className="text-xs text-slate-400 mt-1">8% performance fee</div>
          </div>
        </div>

        {/* Robot Control Panel */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 border border-white/20">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-2xl sm:text-3xl ${robotActive ? 'bg-green-500 animate-pulse' : 'bg-slate-700'}`}>
                🤖
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white">Trading Robot</h3>
                <p className="text-xs sm:text-sm text-slate-300">
                  {robotActive ? '🟢 Active & Scanning' : '⚫ Inactive'}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 sm:gap-3 w-full lg:w-auto">
              <button
                onClick={toggleRobot}
                className={`flex-1 lg:flex-none px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  robotActive
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white'
                }`}
              >
                {robotActive ? '🛑 Deactivate' : '▶️ Activate'}
              </button>
              
              <button
                onClick={intensiveScan}
                disabled={!robotActive || intensiveScanCount >= 20}
                className="flex-1 lg:flex-none px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ⚡ Scan ({intensiveScanCount}/20)
              </button>
            </div>
          </div>
          
          {/* Trading Mode Selector */}
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/20">
            <div className="text-xs sm:text-sm text-slate-300 mb-3">Trading Mode:</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <button
                onClick={() => setTradingMode('manual')}
                className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all duration-300 ${
                  tradingMode === 'manual'
                    ? 'border-indigo-500 bg-indigo-500/20'
                    : 'border-white/20 bg-white/5 hover:bg-white/10'
                }`}
              >
                <div className="text-base sm:text-lg font-bold text-white mb-1">🎮 Manual</div>
                <div className="text-xs text-slate-300">You execute trades manually</div>
              </button>
              
              <button
                onClick={() => setTradingMode('semi-auto')}
                className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all duration-300 ${
                  tradingMode === 'semi-auto'
                    ? 'border-indigo-500 bg-indigo-500/20'
                    : 'border-white/20 bg-white/5 hover:bg-white/10'
                }`}
              >
                <div className="text-base sm:text-lg font-bold text-white mb-1">🔄 Semi-Auto</div>
                <div className="text-xs text-slate-300">AI suggests, you approve</div>
              </button>
              
              <button
                onClick={() => setTradingMode('full-auto')}
                className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all duration-300 ${
                  tradingMode === 'full-auto'
                    ? 'border-indigo-500 bg-indigo-500/20'
                    : 'border-white/20 bg-white/5 hover:bg-white/10'
                }`}
              >
                <div className="text-base sm:text-lg font-bold text-white mb-1">🤖 Full Auto</div>
                <div className="text-xs text-slate-300">AI executes automatically</div>
              </button>
            </div>
          </div>
          
          <div className="mt-3 sm:mt-4 text-center">
            <div className="text-xs sm:text-sm text-slate-400">
              Total Scans: <span className="text-white font-bold">{scanCount}</span>
            </div>
          </div>
        </div>

        {/* Live Arbitrage Scanner */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 border border-white/20">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white">🔍 Live Scanner</h2>
            {robotActive && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm text-green-400 font-semibold">Scanning</span>
              </div>
            )}
          </div>
          
          {opportunities.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <div className="text-4xl sm:text-6xl mb-4">🔍</div>
              <p className="text-sm sm:text-base text-slate-300">
                {robotActive ? 'Scanning for opportunities...' : 'Activate robot to start scanning'}
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {opportunities.map((opp) => (
                <div
                  key={opp.id}
                  className={`bg-gradient-to-r p-4 sm:p-6 rounded-lg sm:rounded-xl border-2 transition-all duration-300 hover:scale-[1.01] ${
                    opp.quality === 'excellent'
                      ? 'from-green-900/40 to-emerald-900/40 border-green-500'
                      : opp.quality === 'good'
                      ? 'from-blue-900/40 to-indigo-900/40 border-blue-500'
                      : 'from-slate-900/40 to-slate-800/40 border-slate-600'
                  }`}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                        <h3 className="text-xl sm:text-2xl font-bold text-white">{opp.pair}</h3>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-bold ${
                          opp.quality === 'excellent'
                            ? 'bg-green-500 text-white'
                            : opp.quality === 'good'
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-600 text-white'
                        }`}>
                          {opp.quality.toUpperCase()}
                        </span>
                        <span className="px-2 sm:px-3 py-1 bg-purple-500 text-white rounded-full text-xs font-bold">
                          AI: {opp.aiScore.toFixed(0)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-3">
                        <div>
                          <div className="text-xs text-slate-400">Buy</div>
                          <div className="text-xs sm:text-sm font-semibold text-white truncate">{opp.buyExchange}</div>
                          <div className="text-base sm:text-lg font-bold text-green-400">${opp.buyPrice.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-400">Sell</div>
                          <div className="text-xs sm:text-sm font-semibold text-white truncate">{opp.sellExchange}</div>
                          <div className="text-base sm:text-lg font-bold text-red-400">${opp.sellPrice.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-400">Spread</div>
                          <div className="text-xl sm:text-2xl font-bold text-amber-400">+{opp.spreadPercent.toFixed(2)}%</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-400">Net Profit</div>
                          <div className="text-xl sm:text-2xl font-bold text-green-400">+{opp.netProfitPercent.toFixed(2)}%</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 text-xs">
                        <div>
                          <span className="text-slate-400">Liquidity:</span>
                          <span className="text-white font-semibold ml-1">{opp.liquidity.toFixed(0)}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Slippage:</span>
                          <span className="text-white font-semibold ml-1">{opp.slippage.toFixed(2)}%</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Speed:</span>
                          <span className="text-white font-semibold ml-1">{opp.executionSpeed}ms</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Volatility:</span>
                          <span className="text-white font-semibold ml-1">{opp.volatility.toFixed(0)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => executeTrade(opp)}
                        disabled={processing || !robotActive || dailyTradeCount >= 20}
                        className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 whitespace-nowrap shadow-lg shadow-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                      >
                        ⚡ Execute Trade
                      </button>
                      <div className="text-xs text-center text-slate-400">
                        Est. Profit: ${(lockedCapital / 20 * opp.netProfitPercent / 100).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Connected Exchanges */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 border border-white/20">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">🌐 Connected Exchanges (20+)</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {exchanges.map((exchange) => (
              <div
                key={exchange.id}
                className="bg-white/5 hover:bg-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/10 transition-all duration-300 cursor-pointer hover:scale-105"
              >
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg p-1 sm:p-1.5 flex items-center justify-center flex-shrink-0">
                      <img 
                        src={exchange.logo} 
                        alt={exchange.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = exchange.icon;
                        }}
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs sm:text-sm font-bold text-white truncate">{exchange.name}</div>
                      <div className="text-xs text-slate-400">{exchange.type}</div>
                    </div>
                  </div>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${exchange.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                </div>
                
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-400">24h Volume:</span>
                    <span className="text-white font-semibold">${exchange.volume24h}B</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Pairs:</span>
                    <span className="text-white font-semibold">{exchange.pairs}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Latency:</span>
                    <span className={`font-semibold ${exchange.latency < 50 ? 'text-green-400' : exchange.latency < 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {exchange.latency}ms
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Fees:</span>
                    <span className="text-white font-semibold">{exchange.fees.taker}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trade History */}
        {tradeHistory.length > 0 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/20">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">📊 Recent Trade History</h2>
            
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left text-xs sm:text-sm text-slate-300 pb-3 px-2 sm:px-0">Time</th>
                      <th className="text-left text-xs sm:text-sm text-slate-300 pb-3 px-2 sm:px-0">Pair</th>
                      <th className="text-left text-xs sm:text-sm text-slate-300 pb-3 px-2 sm:px-0 hidden md:table-cell">Route</th>
                      <th className="text-right text-xs sm:text-sm text-slate-300 pb-3 px-2 sm:px-0 hidden lg:table-cell">Capital</th>
                      <th className="text-right text-xs sm:text-sm text-slate-300 pb-3 px-2 sm:px-0">Profit</th>
                      <th className="text-right text-xs sm:text-sm text-slate-300 pb-3 px-2 sm:px-0 hidden sm:table-cell">Fee</th>
                      <th className="text-right text-xs sm:text-sm text-slate-300 pb-3 px-2 sm:px-0">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradeHistory.map((trade) => (
                      <tr key={trade.id} className="border-b border-white/10">
                        <td className="py-3 text-xs sm:text-sm text-slate-300 px-2 sm:px-0">
                          {new Date(trade.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="py-3 text-xs sm:text-sm font-semibold text-white px-2 sm:px-0">{trade.pair}</td>
                        <td className="py-3 text-xs text-slate-400 px-2 sm:px-0 hidden md:table-cell">
                          {trade.buyExchange} → {trade.sellExchange}
                        </td>
                        <td className="py-3 text-xs sm:text-sm text-right text-white px-2 sm:px-0 hidden lg:table-cell">${trade.capital.toFixed(2)}</td>
                        <td className="py-3 text-xs sm:text-sm text-right text-green-400 px-2 sm:px-0">${trade.profit.toFixed(2)}</td>
                        <td className="py-3 text-xs sm:text-sm text-right text-red-400 px-2 sm:px-0 hidden sm:table-cell">${trade.fee.toFixed(2)}</td>
                        <td className="py-3 text-xs sm:text-sm text-right font-bold text-green-400 px-2 sm:px-0">${trade.netProfit.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showLockModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-6 sm:p-8 max-w-md w-full border border-white/20">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Lock Capital for 30 Days</h3>
            <p className="text-xs sm:text-sm text-slate-300 mb-4 sm:mb-6">
              Lock capital for 30-day arbitrage trading. Minimum $15, Maximum $10,000.
            </p>
            
            <input
              type="number"
              value={lockAmount}
              onChange={(e) => setLockAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
            />
            
            <div className="flex gap-3">
              <button
                onClick={lockCapital}
                disabled={processing}
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
              >
                {processing ? 'Processing...' : 'Lock Capital'}
              </button>
              <button
                onClick={() => setShowLockModal(false)}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddCapitalModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-6 sm:p-8 max-w-md w-full border border-white/20">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Add More Capital</h3>
            <p className="text-xs sm:text-sm text-slate-300 mb-4 sm:mb-6">
              Add more capital to increase trading power. Tier will be extended by 30 days.
            </p>
            
            <input
              type="number"
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
            />
            
            <div className="flex gap-3">
              <button
                onClick={addCapital}
                disabled={processing}
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
              >
                {processing ? 'Processing...' : 'Add & Extend'}
              </button>
              <button
                onClick={() => setShowAddCapitalModal(false)}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-6 sm:p-8 max-w-md w-full border border-white/20">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Withdraw Profit</h3>
            <p className="text-xs sm:text-sm text-slate-300 mb-4 sm:mb-6">
              Withdraw your accumulated profits to main wallet. Capital remains locked.
            </p>
            
            <div className="bg-slate-700 rounded-lg p-4 mb-4">
              <div className="text-sm text-slate-400">Available Profit</div>
              <div className="text-2xl font-bold text-green-400">${totalProfit.toFixed(2)}</div>
            </div>
            
            <input
              type="number"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
            />
            
            <div className="flex gap-3">
              <button
                onClick={withdrawProfit}
                disabled={processing}
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
              >
                {processing ? 'Processing...' : 'Withdraw'}
              </button>
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showTransferModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-6 sm:p-8 max-w-md w-full border border-white/20">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Transfer to Arbitrage</h3>
            <p className="text-xs sm:text-sm text-slate-300 mb-4 sm:mb-6">
              Transfer funds from main wallet to arbitrage wallet for trading.
            </p>
            
            <div className="bg-slate-700 rounded-lg p-4 mb-4">
              <div className="text-sm text-slate-400">Main Wallet Balance</div>
              <div className="text-2xl font-bold text-white">${mainBalance.toFixed(2)}</div>
            </div>
            
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setTransferAmount((mainBalance * 0.25).toFixed(2))}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
              >
                25%
              </button>
              <button
                onClick={() => setTransferAmount((mainBalance * 0.5).toFixed(2))}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
              >
                50%
              </button>
              <button
                onClick={() => setTransferAmount((mainBalance * 0.75).toFixed(2))}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
              >
                75%
              </button>
              <button
                onClick={() => setTransferAmount(mainBalance.toFixed(2))}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
              >
                Max
              </button>
            </div>
            
            <input
              type="number"
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
            />
            
            <div className="flex gap-3">
              <button
                onClick={transferToArbitrage}
                disabled={processing}
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
              >
                {processing ? 'Processing...' : 'Transfer'}
              </button>
              <button
                onClick={() => setShowTransferModal(false)}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showReceiveModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-6 sm:p-8 max-w-md w-full border border-white/20">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Receive from Arbitrage</h3>
            <p className="text-xs sm:text-sm text-slate-300 mb-4 sm:mb-6">
              Transfer funds from arbitrage wallet to main wallet. Cannot withdraw locked capital.
            </p>
            
            <div className="bg-slate-700 rounded-lg p-4 mb-4">
              <div className="text-sm text-slate-400">Arbitrage Wallet Balance</div>
              <div className="text-2xl font-bold text-purple-400">${arbitrageBalance.toFixed(2)}</div>
            </div>
            
            <div className="bg-slate-700 rounded-lg p-4 mb-4">
              <div className="text-sm text-slate-400">Locked Capital</div>
              <div className="text-2xl font-bold text-amber-400">${lockedCapital.toFixed(2)}</div>
            </div>
            
            <div className="bg-slate-700 rounded-lg p-4 mb-4">
              <div className="text-sm text-slate-400">Available to Withdraw</div>
              <div className="text-2xl font-bold text-green-400">${(arbitrageBalance - lockedCapital).toFixed(2)}</div>
            </div>
            
            <input
              type="number"
              value={receiveAmount}
              onChange={(e) => setReceiveAmount(e.target.value)}
              placeholder="Enter amount"
              max={(arbitrageBalance - lockedCapital).toFixed(2)}
              className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
            />
            
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setReceiveAmount(((arbitrageBalance - lockedCapital) * 0.25).toFixed(2))}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
              >
                25%
              </button>
              <button
                onClick={() => setReceiveAmount(((arbitrageBalance - lockedCapital) * 0.5).toFixed(2))}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
              >
                50%
              </button>
              <button
                onClick={() => setReceiveAmount(((arbitrageBalance - lockedCapital) * 0.75).toFixed(2))}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
              >
                75%
              </button>
              <button
                onClick={() => setReceiveAmount((arbitrageBalance - lockedCapital).toFixed(2))}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
              >
                Max
              </button>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={receiveFromArbitrage}
                disabled={processing}
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
              >
                {processing ? 'Processing...' : 'Receive'}
              </button>
              <button
                onClick={() => setShowReceiveModal(false)}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}